import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useState,
} from "react";
import styles from "./TicketProcessingInput.module.scss";
import InputField from "../../../../reusable-components/TNInputField";
import {
  checkboxFields,
  DUPLICATE_TICKET_STATUS,
  fieldConstants,
  getInputFields,
  loadCountOptions,
  processingInputsConstants,
  processingInputsFields,
  quantityTypeOptions,
} from "./constants";
import TNButton from "../../../../reusable-components/TNButton";
import {
  ButtonShape,
  ButtonSize,
} from "../../../../enums/ButtonAttributesEnums";
import { Col, Row, Select } from "antd";
import ConditionalRender from "../../../../reusable-components/ConditionalRender";
import { errorKeyMap } from "../../../../models/TicketProcessingParams/ticketProcessing.model";
import { useFormContext } from "react-hook-form";
import { TicketSubmitAction } from "../../../../enums/TicketProcessingReview.enum";
import TicketUploadServices from "../../../../services/ticketUpload.service";
import { getTruckNumbersOptions } from "../../../TicketUploadView/TicketUploadDrawer/AddTicketForm/constants";
import moment from "moment";
import { DateFormat } from "../../../../enums/dateTimeFormat";
import { getCurrentDate } from "../../../../utils/dateUtils";
import { InputfieldTypes } from "../../../../enums/InputFieldAttributesEnums";
import { disableFutureTime } from "../../../../utils/disabledDateUtil";
import clsx from "clsx";
import { TriggerType } from "../../../../enums/TriggerTypeEnums";
import { allowedKeys } from "../../../../constants/keyboardKeys";
import { stripCurrencyFormatting } from "../../../../Utilities/regex";
import { prefillDataValuesType } from "../../../../Types/ticketProcessingTypes";
import { useHistory } from "react-router-dom";
import { appRoutes } from "../../../../constants/appRoutes";
import { TabDataTypes } from "../../../../enums/ViewListingTypes";

interface TicketProcessingInputProps {
  isManagerReview: boolean;
  signedByCustomer: boolean;
  errorKeys: errorKeyMap;
  prefillData: prefillDataValuesType;
  reviewNotes: string;
  activeTicketId?: string | null;
  reviewMetaData?: string | Object;
  isSubmitted?: boolean;
  handleTicketSubmit: () => void;
  handleManagerReview: () => void;
  handleReplaceTicket: () => void;
}

const TicketProcessingInput = forwardRef(
  (props: TicketProcessingInputProps, ref) => {
    const {
      isManagerReview,
      signedByCustomer,
      errorKeys,
      prefillData,
      reviewNotes,
      activeTicketId,
      reviewMetaData,
      isSubmitted,
      handleTicketSubmit,
      handleManagerReview,
      handleReplaceTicket,
    } = props;

    const { Option } = Select;
    const navigate = useHistory();

    const {
      setValue,
      getValues,
      watch,
      resetField,
      reset,
      trigger,
      formState,
      register,
      unregister,
    } = useFormContext();

    const [dailySummaryChecked, setDailySummaryChecked] = useState(false);
    const [signedByCustomerChecked, setSignedByCustomerChecked] = useState(
      signedByCustomer ?? false,
    );
    const [duplicateStatus, setDuplicateStatus] = useState(undefined);

    const { FetchTicketUploadTrucks } = TicketUploadServices();
    const { data: trucksData } = FetchTicketUploadTrucks();
    const truckOptions = trucksData?.data?.map((item) => ({
      label: item?.truckId,
      value: item?.truckId,
      key: item?.truckId,
    }));

    const resetDynamicFields = (value: boolean) => {
      if (value) {
        resetField(fieldConstants.QUANTITY_NAME);
        resetField(fieldConstants.LOAD_TIME_NAME);
        unregister(fieldConstants.QUANTITY_NAME);
        unregister(fieldConstants.LOAD_TIME_NAME);
        register(fieldConstants.START_TIME_NAME);
        register(fieldConstants.END_TIME_NAME);
        register(fieldConstants.LOAD_COUNT_NAME);
      } else {
        resetField(fieldConstants.START_TIME_NAME);
        resetField(fieldConstants.END_TIME_NAME);
        resetField(fieldConstants.LOAD_COUNT_NAME);
        unregister(fieldConstants.START_TIME_NAME);
        unregister(fieldConstants.END_TIME_NAME);
        unregister(fieldConstants.LOAD_COUNT_NAME);
        register(fieldConstants.QUANTITY_NAME);
        register(fieldConstants.LOAD_TIME_NAME);
      }
    };

    const handleCheckChange = (name: string, value: boolean) => {
      setValue(name, value);
      if (name === fieldConstants.DAILY_SUMMARY_NAME) {
        setDailySummaryChecked(value);
        resetDynamicFields(value);
      } else if (name === fieldConstants.SIGNED_BY_NAME) {
        setSignedByCustomerChecked(value);
      }
    };

    const handleDateChange = (
      date: moment.Moment | null,
      dateString: string,
    ) => {
      if (!date) return;

      const selectedDate = moment(date).startOf("day");
      const currentDate = getCurrentDate().startOf("day");
      const isCurrentDate = selectedDate.isSame(currentDate, "day");

      if (isCurrentDate) {
        if (!!getValues(fieldConstants.LOAD_TIME_NAME))
          resetField(fieldConstants.LOAD_TIME_NAME);
        if (!!getValues(fieldConstants.START_TIME_NAME))
          resetField(fieldConstants.START_TIME_NAME);
        if (!!getValues(fieldConstants.END_TIME_NAME))
          resetField(fieldConstants.END_TIME_NAME);
      }
    };

    const handleNumberEntry = (e, decimalAllowed) => {
      // Allow control and navigation keys
      if (
        allowedKeys?.includes(e?.key) ||
        e?.ctrlKey ||
        e?.altKey ||
        e?.metaKey
      ) {
        return;
      }

      // Allow digitls
      if (/^\d$/.test(e?.key)) {
        return;
      }

      // Allow decimal point if enabled
      if (decimalAllowed && e?.key === ".") {
        const currentValue = e?.target?.value?.toString() || "";
        if (currentValue.includes(".")) {
          e?.preventDefault();
          return;
        }
        return;
      }

      e?.preventDefault();
    };

    const DecimalBlur = (e: React.FocusEvent<HTMLInputElement>) => {
      const stripped = stripCurrencyFormatting(e.target.value);
      const numberValue = Number(stripped);
      if (stripped.trim() === "") {
        setValue(fieldConstants.QUANTITY_NAME, undefined);
        return;
      }
      if (!isNaN(numberValue) && isFinite(numberValue) && numberValue >= 0) {
        setValue(fieldConstants.QUANTITY_NAME, numberValue.toFixed(2));
      } else {
        setValue(fieldConstants.QUANTITY_NAME, undefined);
      }
    };

    const handleLoadCountBlur = (e: React.FocusEvent<HTMLInputElement>) => {
      const numberValue = stripCurrencyFormatting(e.target.value);
      const rounded = Math.round(Number(numberValue));
      if (!isNaN(rounded) && !!rounded) {
        setValue(fieldConstants.LOAD_COUNT_NAME, rounded);
      } else {
        setValue(fieldConstants.LOAD_COUNT_NAME, undefined);
      }
    };

    const handleDuplicateStatus = (updatedStatus) =>
      setDuplicateStatus(updatedStatus);

    useImperativeHandle(ref, () => ({
      resetFormFields: () => {
        handleCheckChange(fieldConstants.DAILY_SUMMARY_NAME, false);
      },
    }));

    useEffect(() => {
      handleCheckChange(fieldConstants.SIGNED_BY_NAME, signedByCustomer);
    }, [signedByCustomer]);

    useEffect(() => {
      if (!!prefillData && Object.keys(prefillData)?.length > 0) {
        handleCheckChange(
          fieldConstants.DAILY_SUMMARY_NAME,
          prefillData?.dailySummary,
        );
        if (!!prefillData?.dailySummary) {
          setValue(fieldConstants.START_TIME_NAME, prefillData?.startTime);
          setValue(fieldConstants.END_TIME_NAME, prefillData?.endTime);
          setValue(fieldConstants.LOAD_COUNT_NAME, prefillData?.loadCount);
        } else {
          setValue(fieldConstants?.LOAD_TIME_NAME, prefillData?.loadTime);
          setValue(fieldConstants?.QUANTITY_NAME, prefillData?.quantity);
        }
        if (!!prefillData?.ticketDate) {
          setValue(
            fieldConstants.TICKET_DATE_NAME,
            moment(
              prefillData?.ticketDate,
              DateFormat.MM_DD_YYYY_SLASHED,
              true,
            ),
          );
        } else {
          setValue(fieldConstants.TICKET_DATE_NAME, null);
        }
        setValue(fieldConstants.TRUCK_NUMBER_NAME, prefillData?.truckNumber);
        setValue(fieldConstants.TICKET_NUMBER_NAME, prefillData?.ticketNumber);
        handleCheckChange(fieldConstants.SIGNED_BY_NAME, signedByCustomer);
      } else {
        reset();
        handleCheckChange(fieldConstants.DAILY_SUMMARY_NAME, false);
        handleCheckChange(fieldConstants.SIGNED_BY_NAME, signedByCustomer);
      }
    }, [activeTicketId, prefillData]);

    useEffect(() => {
      if (reviewMetaData && Object.keys(reviewMetaData).length === 0) {
        handleDuplicateStatus(DUPLICATE_TICKET_STATUS.NOT_DUPLICATE);
      } else if (reviewMetaData?.timeRange) {
        handleDuplicateStatus(DUPLICATE_TICKET_STATUS.APPROVED_DUPLICATE);
      } else if (reviewMetaData?.invoiceId) {
        handleDuplicateStatus(DUPLICATE_TICKET_STATUS.INVOICED_DUPLICATE);
      }
    }, [reviewMetaData]);

    const handleDuplicatedTicket = () => {
      if (duplicateStatus === DUPLICATE_TICKET_STATUS.INVOICED_DUPLICATE) {
        navigate.push({
          pathname: appRoutes.INVOICE_MANAGEMENT,
          state: {
            invoiceId: reviewMetaData?.invoiceId,
          },
        });
      } else if (
        duplicateStatus === DUPLICATE_TICKET_STATUS.APPROVED_DUPLICATE
      ) {
        const formattedRange = `${encodeURIComponent(reviewMetaData.timeRange.startDate)}_${encodeURIComponent(reviewMetaData.timeRange.endDate)}`;

        navigate.push({
          pathname: `${appRoutes.VIEW_TICKET_PROCESSING_REVIEW}${formattedRange}`,
          hash: `#${TabDataTypes.APPROVED}`,
          state: {
            startDate: reviewMetaData?.timeRange?.startDate,
            endDate: reviewMetaData?.timeRange?.endDate,
          },
        });
      }
    };

    return (
      <div className={styles.processingInputsContainer}>
        <div className={styles.processingInputsWrapper}>
          <Row
            justify={processingInputsConstants.ROW_JUSTIFY}
            align={processingInputsConstants.ROW_ALIGN}
          >
            <Col span={7}>
              <div className={styles.checkboxWrapper}>
                {checkboxFields.map((field) => (
                  <InputField
                    type={field.type}
                    label={field.label}
                    name={field.name}
                    disabled={isSubmitted}
                    onChange={(val) => handleCheckChange(field.name, val)}
                    {...(field.name === fieldConstants.SIGNED_BY_NAME
                      ? {
                          value: signedByCustomerChecked,
                        }
                      : {
                          value: dailySummaryChecked,
                          fieldAlert: isManagerReview
                            ? false
                            : errorKeys?.dailySummary,
                        })}
                  />
                ))}
              </div>
            </Col>
            {getInputFields(
              dailySummaryChecked,
              isManagerReview,
              isSubmitted,
              !isManagerReview ? errorKeys : {},
            )?.map((fieldItem, index) => {
              if (fieldItem.visible)
                if (fieldItem.type === InputfieldTypes.LABEL)
                  return (
                    <Col span={fieldItem.span}>
                      <div className={styles.reviewNotesContainer}>
                        <label className={styles.reviewNotesLabel}>
                          {fieldItem.label}
                        </label>
                        <div>{reviewNotes}</div>
                      </div>
                    </Col>
                  );
                else
                  return (
                    <Col span={fieldItem.span}>
                      <div
                        className={clsx(styles.inputFieldContainer, {
                          [styles.textAreaContainer]:
                            fieldItem.type === InputfieldTypes.TEXT_AREA,
                        })}
                      >
                        <InputField
                          type={fieldItem.type}
                          name={fieldItem.name}
                          label={fieldItem.label}
                          maxTextLength={fieldItem.maxTextLength}
                          placeholder={fieldItem.placeholder}
                          datepickerSize={fieldItem.datepickerSize}
                          disabledDates={fieldItem.disabledDates}
                          fieldAlert={fieldItem.fieldAlert}
                          allowClear={fieldItem.allowClear}
                          allowDateClear={fieldItem.allowDateClear}
                          showSearch={fieldItem.showSearch}
                          disabled={fieldItem.disabled}
                          selectedDate={watch(fieldConstants.TICKET_DATE_NAME)}
                          className={fieldItem.className}
                          {...(fieldItem.name ===
                          fieldConstants.TRUCK_NUMBER_NAME
                            ? { options: truckOptions }
                            : fieldItem.name ===
                                fieldConstants.REVIEW_NOTES_NAME
                              ? { textAreaValue: reviewNotes ?? "" }
                              : fieldItem.name ===
                                  fieldConstants.TICKET_DATE_NAME
                                ? {
                                    onDateChange: handleDateChange,
                                    allowTyping: true,
                                  }
                                : fieldItem.name ===
                                    fieldConstants.QUANTITY_NAME
                                  ? {
                                      onKeyDown: (e) =>
                                        handleNumberEntry(e, true),
                                      onBlur: (e) => DecimalBlur(e),
                                    }
                                  : fieldItem.name ===
                                      fieldConstants.LOAD_COUNT_NAME
                                    ? {
                                        onKeyDown: (e) =>
                                          handleNumberEntry(e, false),
                                        onBlur: (e) => handleLoadCountBlur(e),
                                      }
                                    : fieldItem.type ===
                                        InputfieldTypes.TIME_PICKER
                                      ? {
                                          disabledTimes: disableFutureTime(
                                            watch(
                                              fieldConstants.TICKET_DATE_NAME,
                                            ),
                                          ),
                                          allowTyping: true,
                                        }
                                      : {})}
                          {...(fieldItem.name ===
                          fieldConstants.TICKET_NUMBER_NAME
                            ? {
                                onKeyDown: (e) => {
                                  if (
                                    e.key === " " ||
                                    e.key === TriggerType.SPACE
                                  ) {
                                    e.preventDefault();
                                  }
                                },
                                onBlur: (e) => {
                                  const ticketNumber = String(
                                    e.target.value,
                                  ).replace(/\s/g, "");
                                  setValue(
                                    fieldConstants.TICKET_NUMBER_NAME,
                                    ticketNumber,
                                  );
                                },
                              }
                            : {})}
                          {...(fieldItem.name === fieldConstants.LOAD_TIME_NAME
                            ? {
                                customNote: fieldItem.customNote,
                              }
                            : {})}
                        />
                      </div>
                    </Col>
                  );
              else return <></>;
            })}
          </Row>
        </div>
        <div className={styles.processingInputsFooter}>
          <ConditionalRender visible={!isManagerReview}>
            <TNButton
              shape={ButtonShape.DEFAULT}
              size={ButtonSize.LARGE}
              className={processingInputsConstants.MANAGER_REVIEW_BTN_CLASS}
              text={processingInputsConstants.MANAGER_REVIEW_BTN_TXT}
              onClick={() => handleManagerReview(TicketSubmitAction.REVIEW)}
              disabled={isSubmitted}
            />
            <TNButton
              shape={ButtonShape.DEFAULT}
              size={ButtonSize.LARGE}
              className={processingInputsConstants.SUBMIT_BTN_CLASS}
              text={processingInputsConstants.SUBMIT_BTN_TXT}
              onClick={() => handleTicketSubmit(TicketSubmitAction.SUBMIT)}
              disabled={isSubmitted}
            />
          </ConditionalRender>
          <ConditionalRender visible={isManagerReview}>
            {duplicateStatus &&
              duplicateStatus !== DUPLICATE_TICKET_STATUS.NOT_DUPLICATE && (
                <TNButton
                  shape={ButtonShape.DEFAULT}
                  size={ButtonSize.LARGE}
                  className={processingInputsConstants.MANAGER_REVIEW_BTN_CLASS}
                  text={processingInputsConstants.VIEW_ORIGINAL_TICKET}
                  onClick={handleDuplicatedTicket}
                />
              )}
            <TNButton
              shape={ButtonShape.DEFAULT}
              size={ButtonSize.LARGE}
              className={processingInputsConstants.MANAGER_REVIEW_BTN_CLASS}
              text={processingInputsConstants.REPLACE_TKT_BTN_TEXT}
              onClick={handleReplaceTicket}
            />
            <TNButton
              shape={ButtonShape.DEFAULT}
              size={ButtonSize.LARGE}
              className={processingInputsConstants.SUBMIT_BTN_CLASS}
              text={processingInputsConstants.APPROVE_TKT_BTN_TEXT}
              onClick={() => handleTicketSubmit(TicketSubmitAction.APPROVED)}
            />
          </ConditionalRender>
        </div>
      </div>
    );
  },
);

export default TicketProcessingInput;
